import { PageTitle } from '@supplement-bacon/react-toolkit/components'
import { Card, Empty, Space, Spin, Timeline, Typography } from 'antd'
import dayjs from 'dayjs'
import { FC, ReactNode, useEffect, useState } from 'react'
import { PostHogEvent } from '../../models/PostHog'
import { fetchEventsForUser } from '../../services/UserService'
import { capitalizeFirstLetter } from '../../utils/string'

const { Text } = Typography

const DISPLAYED_PROPERTIES = [
  'partySize',
  'date',
  'venueId',
  'venueName',
  '$screen_name',
] as const

const EVENT_NAME_MAPPINGS: Record<string, string> = {
  $screen: 'Screen View',
}

const PROPERTY_KEY_MAPPINGS: Record<string, string> = {
  $screen_name: 'Path',
}

// Utility functions
const formatKey = (text: string): string => {
  if (text in PROPERTY_KEY_MAPPINGS) {
    return PROPERTY_KEY_MAPPINGS[text]
  }

  return text.split('_').map(capitalizeFirstLetter).join(' ')
}

const formatEventName = (text: string): string => {
  if (text in EVENT_NAME_MAPPINGS) {
    return EVENT_NAME_MAPPINGS[text]
  }

  return text.split('-').map(capitalizeFirstLetter).join(' ')
}

const renderEventProperties = (properties: Record<string, any>): ReactNode => {
  return Object.entries(properties)
    .filter(([key]) => DISPLAYED_PROPERTIES.includes(key as any))
    .map(([key, value], index) => (
      <Text code type="secondary" key={`${key}-${index}`}>
        {`${formatKey(key)} : ${value}`}
      </Text>
    ))
}

// Sub-components
const EventCard: FC<{ event: PostHogEvent }> = ({ event }) => (
  <Card styles={{ body: { padding: 10 } }}>
    <Space orientation="vertical" size={0} className="w-full">
      <Space className="w-full space-between align-items-center">
        <Text>{formatEventName(event.event)}</Text>
        <Text type="secondary">{dayjs(event.timestamp).fromNow()}</Text>
      </Space>
      <Space orientation="vertical" size={0}>
        {renderEventProperties(event.properties)}
      </Space>
    </Space>
  </Card>
)

const EventTimeline: FC<{ events: PostHogEvent[] }> = ({ events }) => (
  <Timeline
    mode="start"
    items={events.map((event) => ({
      children: <EventCard event={event} />,
      color: 'gray',
    }))}
  />
)

// Main component
interface Props {
  userId: number
}

const EventList: FC<Props> = ({ userId }) => {
  const [data, setData] = useState<PostHogEvent[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setIsLoading(true)
    fetchEventsForUser(userId)
      .then(setData)
      .finally(() => setIsLoading(false))
  }, [userId])

  if (isLoading) {
    return <Spin />
  }

  const hasEvents = data.length > 0

  return (
    <Space orientation="vertical" className="w-full" size={8}>
      <PageTitle
        level={5}
        title="Analytics events"
        style={{ margin: 0 }}
        toolbar={
          <Text italic type="secondary">
            Last 200 analytics events are displayed
          </Text>
        }
      />
      <Card style={{ maxHeight: 450, overflowY: 'scroll' }}>
        {hasEvents ? (
          <EventTimeline events={data} />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No analytics events to display"
          />
        )}
      </Card>
    </Space>
  )
}

type EventListType = { EventList: typeof EventList }

export { EventList, type EventListType }
